import { render, h, Fragment } from "preact";
import { useEffect } from "preact/hooks";
import {
  ConfigureServices,
  Confirmation,
  Review,
  SelectSchedule,
  SelectService,
  ServiceInformation,
  SetPostalCode,
} from "./screens";
import "./index.css";
import useFormStore from "./store/useFormStore";
import Loader from "./components/Loader";
import PromotionIdleBlock from "./components/promotions/PromotionIdleBlock";
import DataLayerTracker from "./components/DataLayerTracker";

export default function App({ country, franchise, postal_code, extra_info }) {
  // Zustand store - only get what we need in App component
  const currentPostalCode = useFormStore((state) => state.currentPostalCode);
  const setPostalCode = useFormStore((state) => state.setCurrentPostalCode);
  const setFranchise = useFormStore((state) => state.setCurrentFranchise);
  const currentScreen = useFormStore((state) => state.currentScreen);
  const goToNextScreen = useFormStore((state) => state.goToNextScreen);
  const goToPreviousScreen = useFormStore((state) => state.goToPreviousScreen);
  const currentFranchise = useFormStore((state) => state.currentFranchise);
  const isShortLeadForm = useFormStore(
    (state) => state.selectedEstimateType === "on_site_estimate_short",
  );
  const resetAll = useFormStore((state) => state.resetAll);
  const isTransitioning = useFormStore((state) => state.isTransitioning);
  const transitionMessage = useFormStore((state) => state.transitionMessage);
  const setStepsMetadata = useFormStore((state) => state.setStepsMetadata);
  const setIsTransitioning = useFormStore((state) => state.setIsTransitioning);
  const checkFormExpiration = useFormStore(
    (state) => state.checkFormExpiration,
  );
  const setFormExpiredMessage = useFormStore(
    (state) => state.setFormExpiredMessage,
  );
  const setFormStartTimestamp = useFormStore(
    (state) => state.setFormStartTimestamp,
  );

  // Add loaded class to #app element when component mounts
  useEffect(() => {
    const appElement = document.getElementById("app");
    if (appElement) {
      appElement.classList.add("loaded");
    }
  }, []);

  // Clear the loader if the page is restored from bfcache (e.g. back navigation
  // after redirecting to the commercial form), otherwise it stays stuck.
  useEffect(() => {
    const handlePageShow = (event) => {
      if (event.persisted) {
        setIsTransitioning(false);
      }
    };
    window.addEventListener("pageshow", handlePageShow);
    return () => window.removeEventListener("pageshow", handlePageShow);
  }, []);

  // Set franchise and postal code from props on initial load only
  useEffect(() => {
    if (franchise && postal_code) {
      // Check if franchise or postal code has changed - if so, reset the form
      const contextChanged =
        (currentPostalCode && currentPostalCode !== postal_code) ||
        (currentFranchise && currentFranchise.id !== franchise.id);

      if (contextChanged) {
        resetAll();
      }

      setPostalCode(postal_code);
      setFranchise(franchise);

      // Check if franchise is accepting orders
      if (franchise.accepting_orders === false) {
        // Stay on set-postal-code screen
        return;
      }

      if (contextChanged) {
        setFormStartTimestamp();
        goToNextScreen("select-service");
        return;
      }

      // Check for form expiration before skipping to select-service
      if (checkFormExpiration()) {
        resetAll();
        setFormExpiredMessage("Your session expired for inactivity.");
        return;
      }

      // Set form start timestamp for session timeout tracking
      setFormStartTimestamp();

      // Skip to service selection if postal code screen is default and we already have postal code + franchise from server
      if (currentScreen === "set-postal-code") {
        goToNextScreen("select-service");
      } else {
        goToNextScreen(currentScreen);
      }
    }
    // Reset form if we land on confirmation page directly (e.g. from refreshing or coming back to the page after submission)
    if (currentScreen === "confirmation") {
      resetAll();
    }
  }, []);

  // Extract steps metadata and store it
  useEffect(() => {
    const stepsMetadata = Object.entries(steps)
      .filter(([key, step]) => step.title && step.step_number)
      .map(([key, step]) => ({
        key,
        title: step.title,
        step_number: step.step_number,
      }));

    setStepsMetadata(stepsMetadata);
  }, []);

  // Check for form expiration whenever screen changes
  useEffect(() => {
    // Only check if we're past the postal code screen
    if (
      currentScreen !== "set-postal-code" &&
      currentScreen !== "confirmation"
    ) {
      if (checkFormExpiration()) {
        // Reset first, then set message so it shows on the postal code screen
        resetAll();
        setFormExpiredMessage("Your session expired for inactivity.");
      }
    }
  }, [currentScreen]);

  const handleRestart = () => {
    resetAll();
  };

  const steps = {
    "set-postal-code": {
      render: () => {
        return (
          <SetPostalCode
            country={country}
            onNext={(beforeTransition) =>
              goToNextScreen("select-service", beforeTransition)
            }
          />
        );
      },
    },
    "select-service": {
      title: "Select Service",
      step_number: 1,
      render: () => {
        return (
          <SelectService
            franchise={currentFranchise}
            services={currentFranchise?.services ?? []}
            onReset={() => handleRestart()}
            onBack={() => goToPreviousScreen()}
            onNext={() => {
              // Skip to information step if short lead form, otherwise go to configure-services
              const nextScreen = isShortLeadForm
                ? "information"
                : "configure-services";
              goToNextScreen(nextScreen);
            }}
          />
        );
      },
    },
    "configure-services": {
      title: "Service Details",
      step_number: 2,
      render: () => {
        return (
          <ConfigureServices
            onBack={() => goToPreviousScreen()}
            onNext={() => goToNextScreen("schedule-service")}
          />
        );
      },
    },
    "schedule-service": {
      title: "Schedule Services",
      step_number: 3,
      render: () => {
        return (
          <SelectSchedule
            franchise={currentFranchise}
            onReset={() => handleRestart()}
            onBack={() => goToPreviousScreen()}
            onNext={() => goToNextScreen("information")}
          />
        );
      },
    },
    information: {
      title: "Your Information",
      step_number: 4,
      render: () => {
        return (
          <ServiceInformation
            userPreFilledData={extra_info}
            onReset={() => handleRestart()}
            onBack={() => goToPreviousScreen()}
            onNext={() => goToNextScreen("review")}
          />
        );
      },
    },
    review: {
      title: "Review",
      step_number: 5,
      render: () => {
        return (
          <Review
            onReset={() => handleRestart()}
            onBack={() => goToPreviousScreen()}
            onNext={() => goToNextScreen("confirmation")}
          />
        );
      },
    },
    confirmation: {
      render: () => {
        return <Confirmation />;
      },
    },
  };

  const renderScreen = () => {
    if (steps[currentScreen]?.render !== undefined) {
      return steps[currentScreen]?.render();
    }

    return <div>Unknown screen</div>;
  };

  // Render the step with loader overlay when transitioning
  return (
    <>
      {isTransitioning && <Loader message={transitionMessage} />}
      {renderScreen()}
      <PromotionIdleBlock />
      <DataLayerTracker />
    </>
  );
}
